import { Controller } from "@hotwired/stimulus"
import { productsFailure } from "../lib/checkout_status"

const PRODUCTS_RETRY_DELAY = 2000
// Long enough that a slow App Store sheet is not mistaken for a lost one.
const PURCHASE_TIMEOUT = 120000

export default class extends Controller {
  static targets = ["pack", "status"]
  static values = {
    messages: { type: Object, default: {} },
    autoOpen: Boolean,
    resumeUrl: String
  }

  connect() {
    this.connected = true
    if (this.autoOpenValue) this.removeCheckoutParameter()
    this.productsRetried = false
    this.productsListener = (event) => this.handleProducts(event)
    this.purchaseListener = (event) => this.handlePurchase(event)
    this.balanceListener = () => this.handleBalanceChanged()
    window.addEventListener("dropkind:iap-products", this.productsListener)
    window.addEventListener("dropkind:iap-purchase", this.purchaseListener)
    window.addEventListener("dropkind:iap-balance-changed", this.balanceListener)

    this.loadProducts()
  }

  disconnect() {
    this.connected = false
    this.clearPurchaseTimeout()
    window.removeEventListener("dropkind:iap-products", this.productsListener)
    window.removeEventListener("dropkind:iap-purchase", this.purchaseListener)
    window.removeEventListener("dropkind:iap-balance-changed", this.balanceListener)
  }

  loadProducts() {
    const bridge = window.webkit?.messageHandlers?.loadPdfCreditProducts
    if (!bridge) return this.showStatus(this.message("unavailable"))

    this.productsRequestId = this.requestId()
    bridge.postMessage({ requestId: this.productsRequestId })
  }

  purchase(event) {
    const bridge = window.webkit?.messageHandlers?.purchasePdfCredits
    if (!bridge || this.purchaseRequestId) return

    this.purchaseRequestId = this.requestId()
    this.startPurchaseTimeout()
    this.setBusy(true)
    this.showStatus(this.message("purchasing"))
    this.track("paid_pdf_checkout_opened")
    bridge.postMessage({ requestId: this.purchaseRequestId, productId: event.currentTarget.dataset.productId })
  }

  // If the native side is killed mid-purchase the bridge event never arrives and the pack
  // buttons stay disabled with no way out but a reload. Letting go of the UI is safe: StoreKit
  // still replays the transaction through Transaction.updates, so a real purchase is not lost.
  startPurchaseTimeout() {
    this.clearPurchaseTimeout()
    this.purchaseTimeout = window.setTimeout(() => {
      if (!this.connected || !this.purchaseRequestId) return

      this.purchaseRequestId = null
      this.showStatus(this.message("purchase_timed_out"))
      this.setBusy(false)
    }, PURCHASE_TIMEOUT)
  }

  clearPurchaseTimeout() {
    if (this.purchaseTimeout) window.clearTimeout(this.purchaseTimeout)
    this.purchaseTimeout = null
  }

  handleProducts(event) {
    if (!this.connected || event.detail?.requestId !== this.productsRequestId) return
    if (event.detail.status !== "loaded") return this.handleProductsFailure(event.detail.status)

    const products = new Map(event.detail.products.map((product) => [product.productId, product]))
    this.packTargets.forEach((pack) => {
      const product = products.get(pack.dataset.productId)
      if (!product) return
      pack.querySelector("[data-app-store-price]").textContent = product.displayPrice
      pack.disabled = false
    })
    this.showStatus("")
    if (this.autoOpenValue) this.packTargets[0]?.click()
  }

  handleProductsFailure(status) {
    const outcome = productsFailure(status, { retried: this.productsRetried })
    if (outcome.action === "show") return this.showStatus(this.message(outcome.messageKey))

    this.productsRetried = true
    window.setTimeout(() => { if (this.connected) this.loadProducts() }, PRODUCTS_RETRY_DELAY)
  }

  handlePurchase(event) {
    if (!this.connected || event.detail?.requestId !== this.purchaseRequestId) return
    this.clearPurchaseTimeout()
    this.purchaseRequestId = null

    switch (event.detail.status) {
    case "fulfilled":
    case "duplicate":
      this.track("paid_pdf_checkout_completed")
      this.showStatus(this.message("completed"))
      if (this.hasResumeUrlValue) {
        this.resumeUpload()
      } else {
        window.setTimeout(() => window.Turbo?.visit(window.location.href, { action: "replace" }), 500)
      }
      break
    case "pending":
      this.showStatus(this.message("pending"))
      this.setBusy(false)
      break
    case "cancelled":
      this.showStatus("")
      this.setBusy(false)
      break
    default:
      this.showStatus(this.message(event.detail.status))
      this.setBusy(false)
    }
  }

  handleBalanceChanged() {
    if (this.connected && !this.purchaseRequestId) window.Turbo?.visit(window.location.href, { action: "replace" })
  }

  setBusy(busy) {
    this.packTargets.forEach((pack) => { pack.disabled = busy })
  }

  showStatus(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message || ""
  }

  message(key) {
    return this.messagesValue[key] || this.messagesValue.unavailable || ""
  }

  requestId() {
    return window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
  }

  removeCheckoutParameter() {
    const url = new URL(window.location.href)
    url.searchParams.delete("checkout")
    window.history.replaceState(window.history.state, "", url.toString())
  }

  async resumeUpload() {
    try {
      const response = await fetch(this.resumeUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Accept": "text/html",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
        }
      })
      if (!response.ok) throw new Error("resume failed")
      window.Turbo?.visit(response.url, { action: "replace" })
    } catch (_error) {
      this.showStatus(this.message("resume_failed"))
      this.setBusy(false)
    }
  }

  track(event) {
    if (typeof window.gtag === "function") window.gtag("event", event)
  }
}
